import {type UpdateEnsConfigurationRequest} from "../../../store/features/ens/types";

export const ENS_SETTINGS_LIMITS = {
    maxActiveAlertsPerUser: {min: 1, max: 10},
    alertHistoryRetentionDays: {min: 30, max: 1095},
    finalLevelTimeoutSeconds: {min: 60, max: 3600},
    smsConfirmationKeywords: {min: 1, max: 10, maxLength: 20},
} as const;

const SMS_CONFIRMATION_KEYWORD_PATTERN = /^[A-Z0-9]+$/;
export const DTMF_KEYS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '#'] as const;

export const integerRangeMessage = (min: number, max: number) => `Must be a whole number between ${min} and ${max}`;

export const validateIntegerRange = (value: number, min: number, max: number): true | string =>
    Number.isInteger(value) && value >= min && value <= max ? true : integerRangeMessage(min, max);

export const normalizeSmsConfirmationKeywords = (csv: string): string[] =>
    Array.from(
        new Set(
            csv
                .split(',')
                .map((keyword) => keyword.trim().toUpperCase())
                .filter((keyword) => keyword.length > 0),
        ),
    );

export const validateSmsConfirmationKeywords = (csv: string): true | string => {
    const keywords = normalizeSmsConfirmationKeywords(csv);
    const limits = ENS_SETTINGS_LIMITS.smsConfirmationKeywords;
    if (keywords.length < limits.min || keywords.length > limits.max) {
        return `Enter between ${limits.min} and ${limits.max} keywords`;
    }
    if (keywords.some((keyword) => keyword.length > limits.maxLength)) {
        return `Each keyword must be ${limits.maxLength} characters or fewer`;
    }
    if (keywords.some((keyword) => !SMS_CONFIRMATION_KEYWORD_PATTERN.test(keyword))) {
        return 'Keywords may contain only A-Z and 0-9 characters';
    }
    return true;
};

export const toUpdateEnsConfigurationRequest = (
    values: {
        maxActiveAlertsPerUser: number;
        alertHistoryRetentionDays: number;
        finalLevelTimeoutSeconds: number;
        smsConfirmationKeywordsCsv: string;
        dtmfConfirmationKey: string;
        adminOverrideAuditEnabled: boolean;
    },
): UpdateEnsConfigurationRequest => ({
    maxActiveAlertsPerUser: Number(values.maxActiveAlertsPerUser),
    alertHistoryRetentionDays: Number(values.alertHistoryRetentionDays),
    finalLevelTimeoutSeconds: Number(values.finalLevelTimeoutSeconds),
    smsConfirmationKeywords: normalizeSmsConfirmationKeywords(values.smsConfirmationKeywordsCsv),
    dtmfConfirmationKey: values.dtmfConfirmationKey,
    adminOverrideAuditEnabled: values.adminOverrideAuditEnabled,
});
